import { useState, useEffect, useRef } from 'react';
import { Plus, MoreHorizontal, Calendar, GripVertical, Check, Clock, AlertCircle, RefreshCw, Loader2, X, Zap, ZapOff, ListTodo, Sparkles, Paperclip, FileText, Trash2, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import CockpitParticulierBottomNav from '../../components/layout/CockpitParticulierBottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_STYLES = {
  high: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
  low: { bg: 'bg-[#1E3A5F]/10', text: 'text-[#1E3A5F]', icon: Check },
};

const COLUMN_COLORS = {
  todo: 'bg-gray-100 border-gray-300',
  inProgress: 'bg-blue-50 border-blue-300',
  review: 'bg-amber-50 border-amber-300',
  done: 'bg-[#1E3A5F]/5 border-[#1E3A5F]/30',
};

const COLUMN_TITLES = {
  todo: 'À faire',
  inProgress: 'En cours',
  review: 'En attente',
  done: 'Terminé',
};

// Modal de création de tâche - Version mobile plein écran / Desktop centré
function CreateTaskModal({ isOpen, onClose, onSubmit, onAskAbra }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Détection du clavier
  useEffect(() => {
    if (!isOpen || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files);
    const newFiles = uploadedFiles.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: (file.size / 1024).toFixed(1) + ' Ko',
      type: file.type
    }));
    setFiles(prev => [...prev, ...newFiles]);
    toast.success(`${uploadedFiles.length} fichier(s) ajouté(s)`);
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Donne un titre à ta tâche');
      return;
    }
    setSubmitting(true);
    await onSubmit({ 
      title, 
      description, 
      priority, 
      due_date: dueDate || null,
      due_time: dueTime || null,
      files: files
    });
    setSubmitting(false);
    setTitle('');
    setDescription('');
    setPriority('medium');
    setDueDate('');
    setDueTime('');
    setFiles([]);
    onClose();
  };

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        {/* Header */}
        <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
          <button onClick={onClose} className="p-1 text-gray-500">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[15px] font-bold text-gray-900">Nouvelle tâche</h1>
        </header>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          <form id="create-kanban-task-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Titre *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Qu'est-ce que tu dois faire ?"
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ajoute des détails..."
                rows={3}
                className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Échéance</label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                  />
                </div>
                <div className="w-28">
                  <input
                    type="time"
                    value={dueTime}
                    onChange={(e) => setDueTime(e.target.value)}
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Priorité</label>
              <div className="flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {[
                  { value: 'low', label: 'Faible' },
                  { value: 'medium', label: 'Normale' },
                  { value: 'high', label: 'Urgente' }
                ].map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-xs border-2 transition-all whitespace-nowrap ${
                      priority === p.value
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white border-gray-200 text-gray-500'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Fichiers</label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                multiple
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full p-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-500"
              >
                <Paperclip size={20} className="mx-auto mb-1" />
                <span className="text-sm">Ajouter des fichiers</span>
              </button>
              {files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-gray-500" />
                        <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
                      </div>
                      <button type="button" onClick={() => removeFile(file.id)} className="p-1 hover:bg-gray-200 rounded">
                        <X size={14} className="text-gray-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer fixe */}
        <div 
          className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
          style={{ 
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}
        >
          {onAskAbra && (
            <button
              type="button"
              onClick={() => onAskAbra({ title, description, priority, due_date: dueDate, due_time: dueTime })}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
            >
              <Sparkles size={16} />
              Créer avec Abracada
            </button>
          )}
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              form="create-kanban-task-form"
              disabled={submitting}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
            >
              {submitting ? 'Création...' : 'Créer'}
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">Nouvelle tâche</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Titre *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Qu'est-ce que tu dois faire ?"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ajoute des détails..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Échéance</label>
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div className="w-32">
                <input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Priorité</label>
            <div className="flex gap-2">
              {[
                { value: 'low', label: 'Faible' },
                { value: 'medium', label: 'Normale' },
                { value: 'high', label: 'Urgente' }
              ].map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPriority(p.value)}
                  className={`flex-1 py-2.5 rounded-xl font-medium text-sm border-2 transition-all ${
                    priority === p.value
                      ? p.value === 'low' 
                        ? 'bg-gray-100 border-gray-400 text-gray-700'
                        : p.value === 'medium'
                        ? 'bg-orange-50 border-orange-500 text-orange-600'
                        : 'bg-red-50 border-red-500 text-red-600'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fichiers</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              multiple
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full p-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
            >
              <Paperclip size={20} className="mx-auto mb-1" />
              <span className="text-sm">Ajouter des fichiers</span>
            </button>
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map(file => (
                  <div key={file.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
                      <span className="text-xs text-gray-400">{file.size}</span>
                    </div>
                    <button type="button" onClick={() => removeFile(file.id)} className="p-1 hover:bg-gray-200 rounded">
                      <X size={14} className="text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            {onAskAbra && (
              <button
                type="button"
                onClick={() => onAskAbra({ title, description, priority, due_date: dueDate, due_time: dueTime })}
                className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
                style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
              >
                <Sparkles size={16} />
                Demander à Abra
              </button>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {submitting ? 'Création...' : 'Créer la tâche'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Modal de détail/édition de tâche - avec Abra
function TaskDetailModal({ task, isOpen, onClose, onUpdate, onDelete, onAskAbra }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setPriority(task.priority || 'medium');
      // Extract date in yyyy-MM-dd format for HTML date input
      const rawDate = task.due_date_raw || task.due_date || '';
      const dateOnly = rawDate ? rawDate.split('T')[0] : '';
      setDueDate(dateOnly);
      setDueTime(task.due_time || '');
      setFiles(task.files || []);
    }
  }, [task]);

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files);
    const newFiles = uploadedFiles.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: (file.size / 1024).toFixed(1) + ' Ko',
      type: file.type
    }));
    setFiles(prev => [...prev, ...newFiles]);
    toast.success(`${uploadedFiles.length} fichier(s) ajouté(s)`);
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    setSubmitting(true);
    await onUpdate(task.id, { 
      title, 
      description, 
      priority, 
      due_date: dueDate || null,
      due_time: dueTime || null,
      files: files
    });
    setSubmitting(false);
    toast.success('Tâche mise à jour !');
    onClose();
  };

  const handleDelete = async () => {
    if (window.confirm('Supprimer cette tâche ?')) {
      await onDelete(task.id);
      toast.success('Tâche supprimée');
      onClose();
    }
  };

  if (!isOpen || !task) return null;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">Détail de la tâche</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Titre *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Échéance</label>
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div className="w-32">
                <input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Priorité</label>
            <div className="flex gap-2">
              {[
                { value: 'low', label: 'Faible' },
                { value: 'medium', label: 'Normale' },
                { value: 'high', label: 'Urgente' }
              ].map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPriority(p.value)}
                  className={`flex-1 py-2.5 rounded-xl font-medium text-sm border-2 transition-all ${
                    priority === p.value
                      ? p.value === 'low' 
                        ? 'bg-gray-100 border-gray-400 text-gray-700'
                        : p.value === 'medium'
                        ? 'bg-orange-50 border-orange-500 text-orange-600'
                        : 'bg-red-50 border-red-500 text-red-600'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fichiers</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              multiple
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full p-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
            >
              <Paperclip size={20} className="mx-auto mb-1" />
              <span className="text-sm">Ajouter des fichiers</span>
            </button>
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map(file => (
                  <div key={file.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
                      <span className="text-xs text-gray-400">{file.size}</span>
                    </div>
                    <button type="button" onClick={() => removeFile(file.id)} className="p-1 hover:bg-gray-200 rounded">
                      <X size={14} className="text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Demander à Abra */}
          <button
            type="button"
            onClick={() => onAskAbra(task)}
            className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
          >
            <Sparkles size={18} />
            Demander à Abra
          </button>

          {/* Supprimer */}
          <button
            type="button"
            onClick={handleDelete}
            className="w-full py-3 rounded-xl font-medium text-red-500 bg-red-50 border border-red-200 flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"
          >
            <Trash2 size={18} />
            Supprimer cette tâche
          </button>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {submitting ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function TaskCard({ task, onDragStart, onMove, onClick }) {
  const priority = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium;
  const PriorityIcon = priority.icon;

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = (date - now) / (1000 * 60 * 60);
    
    if (diffHours < 0) return 'En retard';
    if (diffHours < 2) return 'Très urgent';
    if (diffHours < 24) return 'Aujourd\'hui';
    if (diffHours < 48) return 'Demain';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
      onClick={() => onClick && onClick(task)}
      className={`bg-white rounded-lg p-3 shadow-sm border cursor-pointer hover:shadow-md transition-shadow group ${
        task.is_overdue ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start gap-2">
        <GripVertical size={16} className="text-gray-300 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 mb-2">{task.title}</p>
          {task.description && (
            <p className="text-xs text-gray-500 mb-2 line-clamp-2">{task.description}</p>
          )}
          
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${priority.bg} ${priority.text}`}>
              <PriorityIcon size={12} />
              {task.priority === 'high' ? 'Urgent' : task.priority === 'medium' ? 'Normal' : 'Faible'}
            </span>
            
            {task.due_date && (
              <span className={`inline-flex items-center gap-1 text-xs ${task.is_overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                <Calendar size={12} />
                {formatDueDate(task.due_date)}
              </span>
            )}
            
            {task.files && task.files.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                <Paperclip size={12} />
                {task.files.length}
              </span>
            )}
          </div>

          {task.tags && task.tags.length > 0 && (
            <div className="flex gap-1 mt-2">
              {task.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Source indicator */}
          {task.source && task.source !== 'manual' && (
            <div className="mt-2">
              <span className="text-xs text-gray-400 italic">
                Auto: {task.source}
              </span>
            </div>
          )}
        </div>
        <button className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity">
          <MoreHorizontal size={16} className="text-gray-400" />
        </button>
      </div>
    </motion.div>
  );
}

function Column({ columnId, tasks, onDragStart, onDragOver, onDrop, onAddTask, onTaskClick }) {
  return (
    <div
      className={`flex-1 min-w-[280px] max-w-[320px] rounded-xl border-2 ${COLUMN_COLORS[columnId]} p-3`}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, columnId)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">{COLUMN_TITLES[columnId]}</h3>
          <span className="px-2 py-0.5 bg-white/80 text-gray-600 text-xs font-medium rounded-full">
            {tasks.length}
          </span>
        </div>
        <button 
          onClick={onAddTask}
          className="p-1 hover:bg-white/50 rounded transition-colors"
        >
          <Plus size={18} className="text-gray-500" />
        </button>
      </div>

      <div className="space-y-2 min-h-[100px]">
        <AnimatePresence>
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} onDragStart={onDragStart} onClick={onTaskClick} />
          ))}
        </AnimatePresence>
        
        {tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <ListTodo size={24} className="mb-2 opacity-50" />
            <p className="text-sm">Aucune tâche</p>
          </div>
        )}
      </div>

      <button 
        onClick={onAddTask}
        className="w-full mt-3 p-2 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
      >
        <Plus size={16} />
        Ajouter une tâche
      </button>
    </div>
  );
}

export default function TachesKanbanPage({ onAskAbra: onAskAbraExternal }) {
  const { user } = useCurrentUser();
  const [tasks, setTasks] = useState({ todo: [], inProgress: [], review: [], done: [] });
  const [stats, setStats] = useState({ total: 0, done: 0, pending: 0, overdue: 0, completion_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [draggedTask, setDraggedTask] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [autoTasksEnabled, setAutoTasksEnabled] = useState(true);

  // Récupérer l'user_id réel depuis localStorage
  const getUserId = () => {
    const ecosystemUser = localStorage.getItem('ecosystem_user');
    if (ecosystemUser) {
      try {
        const parsed = JSON.parse(ecosystemUser);
        return parsed.id || 'demo_user';
      } catch(e) {}
    }
    return 'demo_user';
  };

  const fetchTasks = async () => {
    try {
      const userId = getUserId();
      const isDemo = user?.isDemo || userId === 'demo_user';
      
      const response = await fetch(`${API_URL}/api/cockpit/tasks?user_id=${userId}`);
      const data = await response.json();
      
      // Pour les vrais utilisateurs, on affiche uniquement leurs tâches réelles (qui peuvent être vides)
      // Pour le mode démo, si pas de tâches, on charge les tâches démo
      if (isDemo && (!data.todo?.length && !data.inProgress?.length && !data.review?.length && !data.done?.length)) {
        // Charger les tâches démo
        await fetch(`${API_URL}/api/cockpit/tasks/seed-demo?user_id=demo_user`, { method: 'POST' });
        const demoResponse = await fetch(`${API_URL}/api/cockpit/tasks?user_id=demo_user`);
        const demoData = await demoResponse.json();
        setTasks({
          todo: demoData.todo || [],
          inProgress: demoData.inProgress || [],
          review: demoData.review || [],
          done: demoData.done || []
        });
        setStats(demoData.stats || {});
      } else {
        setTasks({
          todo: data.todo || [],
          inProgress: data.inProgress || [],
          review: data.review || [],
          done: data.done || []
        });
        setStats(data.stats || { total: 0, done: 0, pending: 0, overdue: 0, completion_rate: 0 });
      }
    } catch (error) {
      console.error('Erreur chargement tâches:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Charger les tâches même si user est null (mode démo)
    fetchTasks();
  }, []);

  const handleDragStart = (e, taskId) => {
    setDraggedTask(taskId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetColumnId) => {
    e.preventDefault();
    if (!draggedTask) return;

    let sourceColumnId = null;
    Object.entries(tasks).forEach(([colId, colTasks]) => {
      if (colTasks.find(t => t.id === draggedTask)) {
        sourceColumnId = colId;
      }
    });

    if (!sourceColumnId || sourceColumnId === targetColumnId) {
      setDraggedTask(null);
      return;
    }

    const taskToMove = tasks[sourceColumnId].find(t => t.id === draggedTask);
    setTasks(prev => ({
      ...prev,
      [sourceColumnId]: prev[sourceColumnId].filter(t => t.id !== draggedTask),
      [targetColumnId]: [...prev[targetColumnId], { ...taskToMove, status: targetColumnId }]
    }));

    try {
      const userId = getUserId();
      await fetch(`${API_URL}/api/cockpit/tasks/${draggedTask}/move?new_status=${targetColumnId}&user_id=${userId}`, {
        method: 'PATCH'
      });
      fetchTasks();
    } catch (error) {
      console.error('Erreur déplacement:', error);
      fetchTasks();
    }

    setDraggedTask(null);
  };

  const handleCreateTask = async (taskData) => {
    try {
      const userId = getUserId();
      const response = await fetch(`${API_URL}/api/cockpit/tasks?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: taskData.title,
          description: taskData.description,
          priority: taskData.priority,
          status: 'todo',
          tags: [],
          due_date: taskData.due_date
        })
      });
      
      if (response.ok) {
        toast.success('Tâche créée !');
        fetchTasks();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      console.error('Erreur création tâche:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateTask = async (taskId, taskData) => {
    try {
      const userId = getUserId();
      const response = await fetch(`${API_URL}/api/cockpit/tasks/${taskId}?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: taskData.title,
          description: taskData.description,
          priority: taskData.priority,
          due_date: taskData.due_date,
          due_time: taskData.due_time,
          files: taskData.files
        })
      });
      
      if (response.ok) {
        fetchTasks();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur mise à jour tâche:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const userId = getUserId();
      const response = await fetch(`${API_URL}/api/cockpit/tasks/${taskId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchTasks();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression tâche:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAskAbra = (task) => {
    // Utiliser la fonction externe si disponible (Desktop avec chat flottant)
    if (onAskAbraExternal) {
      onAskAbraExternal(task);
      setSelectedTask(null); // Fermer la modal de détail
    } else {
      // Fallback pour mode standalone
      toast.info(`Demander à Abra pour: "${task.title}"`);
    }
  };

  const toggleAutoTasks = () => {
    setAutoTasksEnabled(!autoTasksEnabled);
    toast.success(autoTasksEnabled ? 'Tâches automatiques désactivées' : 'Tâches automatiques activées');
    // TODO: Sauvegarder cette préférence en base
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const isDemo = user?.isDemo;
  const hasNoTasks = stats.total === 0;

  return (
    <div className="space-y-6">
      {/* Modal création */}
      <CreateTaskModal 
        isOpen={showCreateModal} 
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateTask}
        onAskAbra={onAskAbraExternal ? (taskData) => {
          setShowCreateModal(false);
          onAskAbraExternal({ ...taskData, isNewTask: true });
        } : null}
      />

      {/* Modal détail */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        onUpdate={handleUpdateTask}
        onDelete={handleDeleteTask}
        onAskAbra={handleAskAbra}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Liste de tâches</h1>
          <p className="text-sm text-gray-500 mt-1">
            {stats.total > 0 ? (
              <>
                {stats.done}/{stats.total} tâches terminées
                {stats.overdue > 0 && (
                  <span className="text-red-600 ml-2">• {stats.overdue} en retard</span>
                )}
              </>
            ) : (
              "Aucune tâche pour le moment"
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Toggle Tâches Auto */}
          <button
            onClick={toggleAutoTasks}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
              autoTasksEnabled 
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title={autoTasksEnabled ? 'Désactiver les tâches automatiques' : 'Activer les tâches automatiques'}
          >
            {autoTasksEnabled ? <Zap size={16} /> : <ZapOff size={16} />}
            <span className="hidden sm:inline">Auto</span>
          </button>
          
          <button 
            onClick={fetchTasks}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw size={18} />
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Nouvelle tâche
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {stats.total > 0 && (
        <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-[#1E3A5F] h-full transition-all duration-500"
            style={{ width: `${stats.completion_rate || 0}%` }}
          />
        </div>
      )}

      {/* Info banner - Tâches automatiques */}
      {autoTasksEnabled && !isDemo && (
        <div className="bg-gradient-to-r from-[#FFF8F4] to-[#FEF3E8] border border-[#F97316]/20 rounded-xl p-4 flex items-center gap-3">
          <Zap size={20} className="text-orange-500" />
          <p className="text-sm text-[#1E3A5F]">
            <span className="font-medium">Tâches automatiques activées</span> — Les tâches sont créées automatiquement 
            quand tu publies une annonce, reçois un message, ou confirmes une vente.
          </p>
        </div>
      )}

      {/* Demo banner */}
      {isDemo && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <Sparkles size={20} className="text-amber-500" />
          <div>
            <p className="font-medium text-amber-800">Mode Démonstration</p>
            <p className="text-sm text-amber-700">Les tâches affichées sont des exemples. Crée ton compte pour gérer tes propres tâches.</p>
          </div>
        </div>
      )}

      {/* Empty state banner pour vrais utilisateurs */}
      {!isDemo && hasNoTasks && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6 text-center">
          <div className="w-14 h-14 bg-orange-100 rounded-full mx-auto mb-3 flex items-center justify-center">
            <ListTodo size={28} className="text-orange-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Bienvenue dans ton espace tâches !</h3>
          <p className="text-gray-600 text-sm">
            Glisse-dépose tes tâches entre les colonnes pour suivre ton avancement.
          </p>
        </div>
      )}

      {/* Kanban board - Toujours visible */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {['todo', 'inProgress', 'review', 'done'].map((columnId) => (
          <Column
            key={columnId}
            columnId={columnId}
            tasks={tasks[columnId]}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onAddTask={() => setShowCreateModal(true)}
            onTaskClick={(task) => setSelectedTask(task)}
          />
        ))}
      </div>

      {/* Quick stats */}
      {stats.total > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">{tasks.todo.length}</p>
            <p className="text-sm text-gray-500">À faire</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-2xl font-bold text-blue-600">{tasks.inProgress.length}</p>
            <p className="text-sm text-gray-500">En cours</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-2xl font-bold text-amber-600">{tasks.review.length}</p>
            <p className="text-sm text-gray-500">En attente</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-2xl font-bold text-[#1E3A5F]">{tasks.done.length}</p>
            <p className="text-sm text-gray-500">Terminé</p>
          </div>
        </div>
      )}
      
      {/* Navbar Cockpit Particulier */}
      <CockpitParticulierBottomNav />
    </div>
  );
}
